import { Component, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Guid } from '@app/core/models/Guid';
import { IBatchTemplateDTO } from '@app/core/models/batch-template/batch-template.model';
import { BatchTemplateService } from '@app/core/services/http-services/operative/batch-template.service';
import { MachineGroupService } from '@app/core/services/http-services/operative/machine-group.service';
import { GroupResult, groupBy, State } from '@progress/kendo-data-query';
import { TranslateModule } from '@ngx-translate/core';
import { KENDO_COMBOBOX } from '@progress/kendo-angular-dropdowns';
import { KENDO_FORMFIELD } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { Constants } from '@app/shared/helpers/constants';

@Component({
  selector: 'app-select-batch-template',
  templateUrl: './select-batch-template.component.html',
  styleUrls: ['./select-batch-template.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectBatchTemplateComponent,
      multi: true,
    },
  ],
  imports: [KENDO_FORMFIELD, KENDO_LABEL, KENDO_COMBOBOX, TranslateModule],
})
export class SelectBatchTemplateComponent implements ControlValueAccessor, OnInit {
  machineGroupId = input<number | null>(null);
  machineId = input<number | null>(null);
  data = input<GroupResult[] | null>(null);

  loading = false;
  batchTemplates: GroupResult[] = [];
  private _filteredExternalData: GroupResult[] | null = null;

  get displayData(): GroupResult[] {
    const externalData = this.data();
    if (externalData !== null) {
      return this._filteredExternalData ?? externalData;
    }
    return this.batchTemplates;
  }
  selectedbatchTemplateId: Guid;
  state: State = {
    skip: 0,
    take: 100,
    filter: {
      logic: 'and',
      filters: [],
    },
  };
  label = 'Operative.BatchTemplate';

  private onChanged!: Function;
  private onTouched!: Function;
  private machineIds: number[] = [];

  private readonly batchTemplateService = inject(BatchTemplateService);
  private readonly machineGroupService = inject(MachineGroupService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly filterSubject = new Subject<string>();

  constructor() {
    effect(() => {
      const machineId = this.machineId();
      const machineGroupId = this.machineGroupId();

      // Prefer machineId if provided, otherwise fall back to machineGroupId
      if (machineId) {
        this.machineIds = [machineId];
        this.loadBatchTemplatesByMachineIds();
      } else if (machineGroupId) {
        this.loadMachineGroupAndTemplates(machineGroupId);
      } else {
        this.batchTemplates = [];
        this.loading = false;
      }
    });
  }

  ngOnInit(): void {
    if (!this.machineGroupId()) {
      this.loading = false;
    }

    this.filterSubject
      .pipe(
        debounceTime(Constants.DEFAULT_DEBOUNCE_TIME),
        distinctUntilChanged(),
        filter(searchString => searchString.length >= 3),
        filter(() => this.machineIds.length > 0),
        switchMap(searchString => {
          this.loading = true;
          return this.batchTemplateService.getbatchTemplatesByMachineIds(this.state, this.machineIds);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(templates => {
        this.batchTemplates = groupBy(templates, [{ field: 'machineName' }]) as GroupResult[];
        this.loading = false;
      });
  }

  onChangedVal(batchTemplateId: Guid) {
    this.onTouched();
    this.selectedbatchTemplateId = batchTemplateId;
    this.onChanged(this.selectedbatchTemplateId);
  }

  writeValue(batchTemplateId: Guid): void {
    this.selectedbatchTemplateId = batchTemplateId;
  }

  registerOnChange(onChange: any): void {
    this.onChanged = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  public handleFilter(searchString: string): void {
    if (this.data() !== null) {
      if (!searchString) {
        this._filteredExternalData = null;
        return;
      }
      const lower = searchString.toLowerCase();
      this._filteredExternalData = (this.data() as GroupResult[])
        .map(group => ({
          ...group,
          items: (group.items as IBatchTemplateDTO[]).filter(t =>
            t.templateName?.toLowerCase().includes(lower)
          ),
        }))
        .filter(group => (group.items as IBatchTemplateDTO[]).length > 0);
      return;
    }
    this.filterSubject.next(searchString);
  }

  private loadMachineGroupAndTemplates(machineGroupId: number): void {
    this.loading = true;
    this.machineGroupService
      .queryv2({ machineGroupId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result.data && result.data.length > 0) {
          const machineGroup = result.data[0];
          this.machineIds = machineGroup.machineIds ?? [];
          this.loadBatchTemplatesByMachineIds();
        } else {
          this.loading = false;
          this.batchTemplates = [];
        }
      });
  }

  private loadBatchTemplatesByMachineIds(): void {
    if (this.machineIds.length === 0) {
      this.batchTemplates = [];
      this.loading = false;
      return;
    }

    this.batchTemplateService
      .getbatchTemplatesByMachineIds(this.state, this.machineIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(templates => {
        this.batchTemplates = groupBy(templates, [{ field: 'machineName' }]) as GroupResult[];
        this.loading = false;
      });
  }
}
